import { useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { HttpStatusCodes } from 'enums'
import React, { useEffect, useState } from 'react'

import { setStorageValue } from 'core/local-storage'
import { useOnQueryError } from 'hooks/useOnQueryError'
import { establishPlatformSession } from 'services/platform-auth'
import { getUserInfo } from 'services/user'

import { AuthContext } from './AuthContext'
import { USER_SESSION_LOCAL_STORAGE_KEY, getCurrentUser, signIn, signOut } from './airlockBe'

interface AuthContextProviderProps {
  children: React.ReactNode
}

const AuthContextProvider: React.FC<AuthContextProviderProps> = ({ children }) => {
  const [userSession, setUserSession] = useState<UserAuth | undefined>()

  useEffect(() => {
    setUserSession(getCurrentUser())
  }, [])

  const tokenId: string | undefined = getCurrentUser()?.token

  const {
    data: userInfo,
    error: userInfoError,
    errorUpdateCount: userInfoErrorUpdateCount,
    isFetchedAfterMount: isUserInfoFetchedAfterMount,
    isLoading,
    isFetching
  } = useQuery({
    queryKey: ['me', [tokenId]],
    queryFn: () => getUserInfo(),
    retry: false,
    enabled: !!tokenId
  })

  const logout = () => {
    if (!userInfo) {
      window.location.reload()
      return
    }
    setUserSession(undefined)
    signOut()
  }

  useOnQueryError(
    {
      error: userInfoError,
      errorUpdateCount: userInfoErrorUpdateCount,
      isFetchedAfterMount: isUserInfoFetchedAfterMount
    },
    error => {
      // A 401 means the token has expired or is invalid, so the session can be cleared.
      if ((error as AxiosError).response?.status !== HttpStatusCodes.Unauthorized) return

      logout()
    }
  )

  const login = async (username: string, password: string, otp?: string): Promise<UserAuth | { otp: boolean }> => {
    if (userInfo) throw Error('User is already logged in.')
    const data = await signIn(username, password, otp)

    if (data.otp) {
      return data
    }

    try {
      await establishPlatformSession(username, password)
    } catch (error) {
      // Airlock login still succeeds without it, but datasource lookups will fail, so the failure must be visible.
      console.warn('[Platform session] Failed to establish a platform session on login.', error)
    }

    setStorageValue(USER_SESSION_LOCAL_STORAGE_KEY, JSON.stringify(data))
    setUserSession(getCurrentUser())

    return data
  }

  const autoLogin = async (user: UserAuth) => {
    setStorageValue(USER_SESSION_LOCAL_STORAGE_KEY, JSON.stringify(user))
    setUserSession(user)
  }

  return (
    <AuthContext.Provider value={{ autoLogin, login, logout, user: userSession, isLoading, isFetching }}>
      {children}
    </AuthContext.Provider>
  )
}

export default AuthContextProvider
